import React from 'react'
import { graphql } from 'react-apollo'

import { compose, withHandlers, withPropsOnChange, withState } from 'recompose'

import Button from 'components/Button'
import withHeroText, { HeroTextProps } from 'components/HOCs/withHeroText'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import getDefaultEventData from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import setSpendPrefundAmount from 'graphql-local/mutations/setSpendPrefundAmount'
import spendPrefundAmountQuery from 'graphql-local/queries/spendPrefundAmount'
import { TestIDs } from 'test/utils/testIds'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import { sendToSentry } from 'utils/errors'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'

import {
  ButtonsContainer,
  Container,
  Disclaimer,
  StyledButton,
  Subtitle,
  Title,
} from './styled-components'

const NextButton = withClickTrack<any, any>(TRACKER_IDS.PREFUND.NEXT_BUTTON)(Button)

type Props = {
  prefundAmount: number
  spendPrefundAmount?: number
  setPrefundAmount: (amount: number) => void
  handlePrefundAmountPress?: (index: number) => void
  handleNextPress?: () => void
} & HeroTextProps &
  WithFlowRouterProps

const PREFUND_VALUES = [25, 100, 500]

const enhance = compose<Props, {}>(
  withHeroText,
  withFlowRouter,
  graphql(spendPrefundAmountQuery, {
    props: ({ data: { spendPrefundAmount } }: any) => ({
      spendPrefundAmount: spendPrefundAmount.value,
    }),
  }),
  graphql(setSpendPrefundAmount, {
    props: ({ mutate }) => ({
      updateSpendPrefundAmount: mutate,
    }),
  }),
  withState('prefundAmount', 'setPrefundAmount', props =>
    props.spendPrefundAmount ? props.spendPrefundAmount : PREFUND_VALUES[0],
  ),
  withHandlers({
    handleUpdateSpendPrefundAmount: ({ prefundAmount, updateSpendPrefundAmount }) => () => {
      return updateSpendPrefundAmount({
        variables: {
          input: {
            value: prefundAmount,
          },
        },
      }).catch(error => {
        sendToSentry(error, {
          name: 'updating Spend Prefund Amount',
          message: 'Failed to set the users Prefund intent to local cache',
        })
      })
    },
  }),
  withHandlers({
    handlePrefundAmountPress: ({ setPrefundAmount }) => index => () => {
      setPrefundAmount(PREFUND_VALUES[index])
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.PREFUND.PREFUND_AMOUNT_BUTTON),
          ...getDefaultEventData('Button Tapped'),
          cta_title: `$${PREFUND_VALUES[index]}`,
          object_name: 'registrationSpendSpendPrefundAmount',
          screen: 'registrationSpendSpendPrefund',
          screen_name: 'registrationSpendSpendPrefund',
        },
      })
    },
    handleNextPress: ({ route, handleUpdateSpendPrefundAmount }) => () => {
      handleUpdateSpendPrefundAmount().then(() => {
        route({ from: FlowRoute.Prefund, action: 'continue' })
      })
    },
  }),
  withPropsOnChange(['prefundAmount'], ({ handleUpdateSpendPrefundAmount }) =>
    handleUpdateSpendPrefundAmount(),
  ),
)

const Prefund: React.SFC<Props> = ({
  heroText,
  prefundAmount,
  handleNextPress,
  handlePrefundAmountPress,
}) => (
  <>
    <Nav />
    <Layout>
      <Container>
        <Title data-testid={TestIDs.SPEND_PREFUND.HEADER}>{heroText.header}</Title>
        <Subtitle data-testid={TestIDs.SPEND_PREFUND.TEXT}>{heroText.text}</Subtitle>
        <ButtonsContainer>
          {PREFUND_VALUES.map((amount, index) => (
            <StyledButton
              secondary={index !== PREFUND_VALUES.indexOf(prefundAmount)}
              onPress={handlePrefundAmountPress(index)}
              key={index}
              data-testid={`$${amount}_${TestIDs.SPEND_PREFUND.PREFUND_AMOUNT}`}
            >{`$${amount}`}</StyledButton>
          ))}
        </ButtonsContainer>
        <Disclaimer>
          These funds will transfer to Acorns Spend as soon as your account has been approved.
        </Disclaimer>
        <Separator />
        <NextButton onPress={handleNextPress} data-testid={TestIDs.SPEND_PREFUND.NEXT_BUTTON}>
          Next
        </NextButton>
      </Container>
    </Layout>
  </>
)

export default enhance(Prefund)
